// @ts-nocheck
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { getGoogle } from '../lib/maps/placesClient';

interface RestaurantMapProps {
  address?: string;
  location?: string;
  restaurantName?: string;
}

const containerStyle: React.CSSProperties = {
  width: '100%',
  height: '300px',
  borderRadius: '1rem',
  overflow: 'hidden',
};

const RestaurantMap: React.FC<RestaurantMapProps> = ({ address, location, restaurantName }) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [center, setCenter] = useState<google.maps.LatLngLiteral>({ lat: 0, lng: 0 });
  const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral | null>(null);
  const [zoom, setZoom] = useState<number>(2);

  useEffect(() => {
    let mounted = true;
    getGoogle()
      .then(() => mounted && setReady(true))
      .catch((e) => mounted && setError(e?.message || 'Error loading Google Maps'));
    return () => {
      mounted = false;
    };
  }, []);

  const resolvePlace = useCallback((map: google.maps.Map) => {
    const query = `${restaurantName || ''} ${address || ''} ${location || ''}`.trim();
    if (!query) {
      const world = { lat: 0, lng: 0 };
      setCenter(world);
      setMarkerPosition(null);
      setZoom(2);
      return;
    }

    const service = new google.maps.places.PlacesService(map);

    const applyResult = (place: google.maps.places.PlaceResult | undefined) => {
      const loc = place?.geometry?.location;
      if (loc) {
        const latLng = { lat: loc.lat(), lng: loc.lng() };
        setCenter(latLng);
        setMarkerPosition(latLng);
        setZoom(15);
      } else {
        const world = { lat: 0, lng: 0 };
        setCenter(world);
        setMarkerPosition(null);
        setZoom(2);
      }
    };

    const findReq: google.maps.places.FindPlaceFromQueryRequest = {
      query,
      fields: ['geometry', 'name'],
    };
    service.findPlaceFromQuery(findReq, (results, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
        applyResult(results[0]);
      } else {
        const textReq: google.maps.places.TextSearchRequest = { query };
        service.textSearch(textReq, (tsResults, tsStatus) => {
          if (tsStatus === google.maps.places.PlacesServiceStatus.OK && tsResults && tsResults.length > 0) {
            applyResult(tsResults[0]);
          } else {
            applyResult(undefined);
          }
        });
      }
    });
  }, [address, location, restaurantName]);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    resolvePlace(map);
  }, [resolvePlace]);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  useEffect(() => {
    if (ready && mapRef.current) {
      resolvePlace(mapRef.current);
    }
  }, [ready, resolvePlace]);

  if (error) {
    return <div className="text-red-500 text-center p-4">{error}</div>;
  }
  if (!ready) {
    return <div className="text-gray-500 text-center p-4">Cargando mapa...</div>;
  }

  return (
    <div className="mt-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-3">Ubicación del Restaurante</h3>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={zoom}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{ disableDefaultUI: true }}
      >
        {markerPosition && <Marker position={markerPosition} />}
      </GoogleMap>
    </div>
  );
};

export default RestaurantMap;